import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://zuyvaptdwlnapdstqndu.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp1eXZhcHRkd2xuYXBkc3RxbmR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE2MzY1OTMsImV4cCI6MjA2NzIxMjU5M30.keDOvSOjEjq0J4S2uamg7xOScX-eW6XOksZpyIvBP7w'

// Debug mode for demo purposes
const isDemoMode = import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === 'true'

// In demo mode, we'll disable Supabase features
const isDemo = supabaseAnonKey === 'DEMO_MODE' || !supabaseUrl || !supabaseAnonKey

if (isDemo) {
  console.warn('🚨 DEMO MODE: Supabase authentication disabled. Create .env file with your Supabase keys to enable user accounts.')
}

// Export demo mode flag
export const isDemoModeFlag = isDemo

// Create Supabase client or mock for demo mode
export const supabase = isDemo 
  ? {
      auth: {
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        signUp: () => Promise.resolve({ data: null, error: { message: 'Demo mode: Please configure Supabase' } }),
        signInWithPassword: () => Promise.resolve({ data: null, error: { message: 'Demo mode: Please configure Supabase' } }),
        signOut: () => Promise.resolve({ error: null }),
        resetPasswordForEmail: () => Promise.resolve({ data: null, error: { message: 'Demo mode: Please configure Supabase' } })
      },
      from: (table: string) => ({
        select: (columns = '*') => ({
          eq: (column: string, value: any) => ({
            single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } }),
            order: (column: string, options?: any) => ({
              range: (start: number, end: number) => Promise.resolve({ data: [], error: { message: 'Database not available' } }),
              single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
            })
          }),
          gte: (column: string, value: any) => ({
            single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } }),
            order: (column: string, options?: any) => ({
              range: (start: number, end: number) => Promise.resolve({ data: [], error: { message: 'Database not available' } }),
              single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
            })
          }),
          order: (column: string, options?: any) => ({
            range: (start: number, end: number) => Promise.resolve({ data: [], error: { message: 'Database not available' } }),
            single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
          }),
          range: (start: number, end: number) => Promise.resolve({ data: [], error: { message: 'Database not available' } }),
          single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } }),
          limit: (count: number) => ({
            single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } }),
            order: (column: string, options?: any) => ({
              range: (start: number, end: number) => Promise.resolve({ data: [], error: { message: 'Database not available' } }),
              single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
            })
          })
        }),
        insert: (values: any) => ({
          select: (columns = '*') => ({
            single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
          })
        }),
        update: (values: any) => ({
          eq: (column: string, value: any) => ({
            select: (columns = '*') => ({
              single: () => Promise.resolve({ data: null, error: { message: 'Database not available' } })
            })
          })
        }),
        delete: () => ({
          eq: (column: string, value: any) => Promise.resolve({ error: { message: 'Database not available' } })
        })
      }),
      rpc: () => Promise.resolve({ data: null, error: { message: 'Demo mode' } }),
      functions: {
        invoke: () => Promise.resolve({ data: null, error: { message: 'Demo mode' } })
      }
    }
  : createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storage: window.localStorage,
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    })

// Database types
export interface Profile {
  id: string
  email: string
  username?: string
  display_name?: string
  avatar_url?: string
  bio?: string
  subscription_tier: 'free' | 'premium' | 'pro'
  total_images_generated: number
  generation_count?: number
  tier?: 'free' | 'premium' | 'pro'
  created_at: string
  updated_at: string
}

export interface ToefettiImage {
  id: string
  user_id: string
  title: string
  description?: string
  prompt_text: string
  image_url: string
  thumbnail_url?: string
  reference_images?: string[] // Array of URLs to reference images
  filters_used: Record<string, any>
  voice_persona: Record<string, any>
  generation_settings: Record<string, any>
  is_public: boolean
  is_nsfw: boolean
  likes_count: number
  views_count: number
  download_count: number
  created_at: string
  updated_at: string
}

export interface Collection {
  id: string
  user_id: string
  name: string
  description?: string
  is_public: boolean
  cover_image_id?: string
  created_at: string
  updated_at: string
}

export interface Comment {
  id: string
  user_id: string
  image_id: string
  content: string
  parent_id?: string
  is_deleted: boolean
  created_at: string
  updated_at: string
  profiles?: {
    username?: string
    display_name?: string
  }
  replies?: Comment[]
}

export interface Favorite {
  id: string
  user_id: string
  image_id: string
  created_at: string
}

// Auth helpers
export const getCurrentUser = () => supabase.auth.getUser()
export const signOut = () => supabase.auth.signOut()

// Profile helpers
export const getProfile = async (userId: string): Promise<Profile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (error) {
      console.warn('Profile table may not exist or user profile not found:', error.message)
      
      // If table doesn't exist or profile not found, return null for now
      // In a real app, you'd want to handle database setup here
      return null
    }
    
    return data
  } catch (error) {
    console.error('Error fetching profile:', error)
    return null
  }
}

export const ensureProfile = async (user: any): Promise<Profile | null> => {
  if (!user) return null
  
  try {
    console.log('🔍 Checking if user profile exists for:', user.id)
    
    // Try to get existing profile
    let profile = await getProfile(user.id)
    
    if (profile) {
      console.log('✅ User profile found:', profile.display_name || profile.username || user.email)
      return profile
    }
    
    // Profile doesn't exist - this means the database trigger didn't run
    // or the user signed up before the trigger was created
    console.log('❌ User profile not found. This usually means:')
    console.log('   1. Database trigger is not set up properly')
    console.log('   2. User signed up before trigger was created')
    console.log('   3. Database tables need to be set up')
    
    // Don't try to create profile directly due to RLS policies
    // Instead, return null and let the UI handle the database setup flow
    return null
    
  } catch (error) {
    console.error('❌ Error checking user profile:', error)
    return null
  }
}

export const updateProfile = async (userId: string, updates: Partial<Profile>) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    
    if (error) {
      console.error('Error updating profile:', error)
      return null
    }
    
    return data
  } catch (error) {
    console.error('Error updating profile:', error)
    return null
  }
}

// Helper function to upload reference images to Supabase Storage
export const uploadReferenceImages = async (images: File[], userId: string): Promise<string[]> => {
  const uploadedUrls: string[] = []
  
  try {
    for (const image of images) {
      // Generate unique filename
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const fileExtension = image.name.split('.').pop()
      const fileName = `reference_${userId}_${timestamp}_${randomString}.${fileExtension}`
      const filePath = `reference-images/${fileName}`
      
      console.log(`📤 Uploading reference image: ${fileName}`)
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('images')
        .upload(filePath, image, {
          cacheControl: '3600',
          upsert: false
        })
      
      if (error) {
        console.error('❌ Error uploading reference image:', error)
        continue // Skip this image but continue with others
      }
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath)
      
      uploadedUrls.push(publicUrl)
      console.log(`✅ Reference image uploaded: ${publicUrl}`)
    }
    
    return uploadedUrls
  } catch (error) {
    console.error('❌ Error uploading reference images:', error)
    return uploadedUrls // Return whatever was successfully uploaded
  }
}

// Image helpers - will handle gracefully if tables don't exist
export const saveImage = async (imageData: Omit<ToefettiImage, 'id' | 'created_at' | 'updated_at'>, uploadedImages?: File[]) => {
  try {
    console.log('💾 Attempting to save image to database:', {
      user_id: imageData.user_id,
      title: imageData.title,
      prompt_text: imageData.prompt_text?.substring(0, 50) + '...',
      image_url: imageData.image_url ? 'Present' : 'Missing',
      reference_images_count: uploadedImages?.length || 0
    })
    
    // First ensure the user profile exists
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      console.warn('❌ No authenticated user found, cannot save image')
      return null
    }
    
    // Ensure user profile exists
    const profile = await ensureProfile(user)
    if (!profile) {
      console.warn('❌ Could not create/find user profile, cannot save image')
      return null
    }
    
    console.log('✅ User profile confirmed, proceeding with image save')
    
    // Download and store the generated image permanently if it's a temporary URL
    let permanentImageUrl = imageData.image_url
    if (imageData.image_url && (
      imageData.image_url.includes('oaidalleapiprodscus.blob.core.windows.net') || 
      imageData.image_url.includes('dalleproduse.blob.core.windows.net') ||
      imageData.image_url.includes('dall-e-') ||
      imageData.image_url.includes('openai')
    )) {
      console.log('🔄 Detected temporary OpenAI URL, downloading and storing permanently...')
      const storedUrl = await downloadAndStoreImage(imageData.image_url, user.id, imageData.generation_settings)
      if (storedUrl) {
        permanentImageUrl = storedUrl
        console.log('✅ Image stored permanently, using new URL')
      } else {
        console.warn('⚠️ Failed to store image permanently, using original URL')
      }
    }
    
    // Upload reference images if provided
    let referenceImageUrls: string[] = []
    if (uploadedImages && uploadedImages.length > 0) {
      console.log(`📤 Uploading ${uploadedImages.length} reference images...`)
      referenceImageUrls = await uploadReferenceImages(uploadedImages, user.id)
      console.log(`✅ ${referenceImageUrls.length} reference images uploaded successfully`)
    }
    
    // Add reference images and permanent URL to the image data
    const imageDataWithReferences = {
      ...imageData,
      image_url: permanentImageUrl,
      reference_images: referenceImageUrls.length > 0 ? referenceImageUrls : null
    }
    
    const { data, error } = await supabase
      .from('images')
      .insert(imageDataWithReferences)
      .select()
      .single()
    
    if (error) {
      console.warn('❌ Unable to save image to database:', error.message)
      console.warn('Error details:', error)
      return null
    }
    
    console.log('✅ Image saved successfully to database:', {
      id: data.id,
      created_at: data.created_at,
      reference_images_count: data.reference_images?.length || 0,
      permanent_url: permanentImageUrl !== imageData.image_url ? 'Converted to permanent' : 'Already permanent'
    })
    
    return data
  } catch (error) {
    console.error('❌ Error saving image:', error)
    return null
  }
}

export const getUserImages = async (userId: string, limit = 20, offset = 0) => {
  try {
    console.log('🔍 Fetching user images from database:', {
      userId,
      limit,
      offset
    })
    
    // First ensure the user profile exists
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      console.warn('❌ No authenticated user found, cannot fetch images')
      return []
    }
    
    // Ensure user profile exists
    const profile = await ensureProfile(user)
    if (!profile) {
      console.warn('❌ Could not create/find user profile, cannot fetch images')
      return []
    }
    
    console.log('✅ User profile confirmed, fetching images')
    
    const { data, error } = await supabase
      .from('images')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
    
    if (error) {
      console.warn('❌ Unable to fetch user images:', error.message)
      console.warn('Error details:', error)
      return []
    }
    
    console.log('✅ User images fetched successfully:', {
      count: data?.length || 0,
      firstImage: data?.[0] ? {
        id: data[0].id,
        title: data[0].title,
        created_at: data[0].created_at
      } : 'None'
    })
    
    return data || []
  } catch (error) {
    console.error('❌ Error fetching user images:', error)
    return []
  }
}

export const deleteImage = async (imageId: string) => {
  try {
    const { error } = await supabase
      .from('images')
      .delete()
      .eq('id', imageId)
    
    if (error) {
      console.error('Error deleting image:', error)
      throw error
    }
    
    return true
  } catch (error) {
    console.error('Error deleting image:', error)
    throw error
  }
}

export const updateImage = async (imageId: string, updates: Partial<ToefettiImage>) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('Must be logged in to update image')
    }
    
    const { data, error } = await supabase
      .from('images')
      .update(updates)
      .eq('id', imageId)
      .eq('user_id', user.id) // Ensure user can only update their own images
      .select()
      .single()
    
    if (error) {
      console.error('Error updating image:', error)
      throw error
    }
    
    return data
  } catch (error) {
    console.error('Error updating image:', error)
    throw error
  }
}

export const getPublicImages = async (limit = 20, offset = 0) => {
  try {
    const { data, error } = await supabase
      .from('images')
      .select(`
        *,
        profiles (
          username,
          display_name
        )
      `)
      .eq('is_public', true)
      .eq('is_nsfw', false)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)
    
    if (error) {
      console.warn('Unable to fetch public images (tables may not exist):', error.message)
      return []
    }
    
    return data || []
  } catch (error) {
    console.error('Error fetching public images:', error)
    return []
  }
}

// Comment functions
export const getImageComments = async (imageId: string) => {
  try {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        profiles (
          username,
          display_name
        )
      `)
      .eq('image_id', imageId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true })
    
    if (error) {
      console.warn('Unable to fetch comments:', error.message)
      return []
    }
    
    // Organize comments into threads (parent comments with replies)
    const comments = data || []
    const parentComments = comments.filter(comment => !comment.parent_id)
    const replies = comments.filter(comment => comment.parent_id)
    
    // Attach replies to parent comments
    const threaded = parentComments.map(parent => ({
      ...parent,
      replies: replies.filter(reply => reply.parent_id === parent.id)
    }))
    
    return threaded
  } catch (error) {
    console.error('Error fetching comments:', error)
    return []
  }
}

export const addComment = async (imageId: string, content: string, parentId?: string) => {
  try {
    console.log('🔄 addComment called with:', { imageId, content: content.substring(0, 50), parentId })
    
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      console.error('❌ Authentication error:', authError)
      throw new Error('Must be logged in to comment')
    }
    
    console.log('✅ User authenticated:', { id: user.id, email: user.email })
    
    const commentData = {
      user_id: user.id,
      image_id: imageId,
      content: content.trim(),
      parent_id: parentId || null
    }
    
    console.log('📤 Inserting comment with data:', commentData)
    
    const { data, error } = await supabase
      .from('comments')
      .insert(commentData)
      .select(`
        *,
        profiles (
          username,
          display_name
        )
      `)
      .single()
    
    if (error) {
      console.error('❌ Database error adding comment:', error)
      throw error
    }
    
    console.log('✅ Comment inserted successfully:', data)
    return data
  } catch (error) {
    console.error('❌ Error in addComment function:', error)
    throw error
  }
}

export const deleteComment = async (commentId: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('Must be logged in to delete comment')
    }
    
    // Soft delete by setting is_deleted to true
    const { error } = await supabase
      .from('comments')
      .update({ is_deleted: true })
      .eq('id', commentId)
      .eq('user_id', user.id) // Ensure user can only delete their own comments
    
    if (error) {
      console.error('Error deleting comment:', error)
      throw error
    }
    
    return true
  } catch (error) {
    console.error('Error deleting comment:', error)
    throw error
  }
}

export const updateComment = async (commentId: string, content: string) => {
  try {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('Must be logged in to edit comment')
    }
    
    const { data, error } = await supabase
      .from('comments')
      .update({ content: content.trim() })
      .eq('id', commentId)
      .eq('user_id', user.id) // Ensure user can only edit their own comments
      .select(`
        *,
        profiles (
          username,
          display_name
        )
      `)
      .single()
    
    if (error) {
      console.error('Error updating comment:', error)
      throw error
    }
    
    return data
  } catch (error) {
    console.error('Error updating comment:', error)
    throw error
  }
}

// Favorites helpers
export const toggleFavorite = async (userId: string, imageId: string) => {
  try {
    // Check if already favorited
    const { data: existing } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('image_id', imageId)
      .single()
    
    if (existing) {
      // Remove favorite
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', userId)
        .eq('image_id', imageId)
      
      return !error
    } else {
      // Add favorite
      const { error } = await supabase
        .from('favorites')
        .insert({ user_id: userId, image_id: imageId })
      
      return !error
    }
  } catch (error) {
    console.warn('Unable to toggle favorite (tables may not exist):', error)
    return false
  }
}

export const getUserFavorites = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .select(`
        *,
        images (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.warn('Unable to fetch favorites (tables may not exist):', error.message)
      return []
    }
    
    return data || []
  } catch (error) {
    console.error('Error fetching favorites:', error)
    return []
  }
}

// OpenAI API Key retrieval from Supabase secrets
export const getOpenAIApiKey = async (): Promise<string | null> => {
  if (isDemoModeFlag) {
    console.warn('Demo mode: OpenAI API key not available')
    return null
  }

  try {
    console.log('🔑 Attempting to retrieve AI API key from Supabase Edge Function...')
    
    // Get the current session to use the proper JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()
    
    if (sessionError || !session) {
      console.warn('No active session found, cannot retrieve API key')
      return null
    }
    
    console.log('✅ Active session found, calling Edge Function...')
    
    // Add timeout wrapper
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Timeout: Edge Function call took too long (15s)')), 15000)
    )

    // Use Supabase Edge Function to retrieve the secret with proper auth
    const apiCallPromise = supabase.functions.invoke('get-openai-key', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        userId: session.user.id
      })
    })

    const result = await Promise.race([apiCallPromise, timeoutPromise])
    const { data, error } = result as any

    if (error) {
      console.error('❌ Error calling Edge Function:', error)
      return null
    }

    if (data?.error) {
      console.error('❌ Edge Function returned error:', data.error)
      console.error('Debug info:', data.debug)
      return null
    }

    if (data?.apiKey) {
      console.log('✅ Successfully retrieved AI API key from Supabase')
      console.log('🔑 Key preview:', data.keyPreview)
      console.log('👤 User email:', data.userEmail)
      console.log('🛡️ Admin role:', data.adminRole)
      return data.apiKey
    } else {
      console.warn('❌ OpenAI API key not found in response')
      console.log('Response data:', data)
      return null
    }
  } catch (error) {
    console.error('❌ Error calling Supabase function for OpenAI key:', error)
    
    if (error instanceof Error) {
      if (error.message.includes('Timeout')) {
        console.error('🕐 Edge Function timed out - this may indicate deployment issues')
      } else if (error.message.includes('NetworkError')) {
        console.error('🌐 Network error - check internet connection')
      } else {
        console.error('🔧 Unknown error:', error.message)
      }
    }
    
    console.error('💡 Troubleshooting tips:')
    console.error('1. Ensure Edge Function is deployed: supabase functions deploy get-openai-key')
    console.error('2. Check OpenAI API key is set in Supabase secrets')
    console.error('3. Verify user authentication is working')
    
    // Don't throw error, just return null to fallback to mock generation
    return null
  }
}

// Admin helpers
export interface AdminUser {
  id: string
  user_id: string | null
  email: string
  role: 'admin' | 'super_admin' | 'moderator'
  permissions: Record<string, any>
  is_active: boolean
  created_at: string
  updated_at: string
}

export const isUserSuperAdmin = async (email?: string): Promise<boolean> => {
  try {
    if (isDemoModeFlag) return false
    
    const { data, error } = await supabase.rpc('get_user_admin_role', {
      check_email: email || null
    })
    
    if (error) {
      console.warn('Error checking admin role:', error.message)
      return false
    }
    
    return data === 'super_admin'
  } catch (error) {
    console.error('Error checking super admin status:', error)
    return false
  }
}

export const isUserAdmin = async (email?: string): Promise<boolean> => {
  try {
    if (isDemoModeFlag) return false
    
    const { data, error } = await supabase.rpc('is_user_admin', {
      check_email: email || null
    })
    
    if (error) {
      console.warn('Error checking admin status:', error.message)
      return false
    }
    
    return data === true
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

export const getUserAdminRole = async (email?: string): Promise<string> => {
  try {
    if (isDemoModeFlag) return 'user'
    
    const { data, error } = await supabase.rpc('get_user_admin_role', {
      check_email: email || null
    })
    
    if (error) {
      console.warn('Error getting admin role:', error.message)
      return 'user'
    }
    
    return data || 'user'
  } catch (error) {
    console.error('Error getting admin role:', error)
    return 'user'
  }
}

// Helper function to download and store generated images permanently
export const downloadAndStoreImage = async (imageUrl: string, userId: string, metadata?: any): Promise<string | null> => {
  try {
    console.log('📥 Downloading generated image from temporary URL...')
    
    // Download the image from the temporary URL
    const response = await fetch(imageUrl, {
      mode: 'cors',
      credentials: 'omit'
    })
    
    if (!response.ok) {
      console.error('❌ Failed to download image:', response.status, response.statusText)
      return null
    }
    
    const blob = await response.blob()
    console.log('✅ Image downloaded, size:', blob.size, 'bytes')
    
    // Generate unique filename
    const timestamp = Date.now()
    const randomString = Math.random().toString(36).substring(2, 15)
    const fileName = `generated_${userId}_${timestamp}_${randomString}.png`
    const filePath = `generated-images/${fileName}`
    
    console.log(`📤 Uploading to Supabase Storage: ${fileName}`)
    
    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('images')
      .upload(filePath, blob, {
        cacheControl: '31536000', // Cache for 1 year
        upsert: false,
        contentType: 'image/png'
      })
    
    if (error) {
      console.error('❌ Error uploading to Supabase Storage:', error)
      return null
    }
    
    // Get permanent public URL
    const { data: { publicUrl } } = supabase.storage
      .from('images')
      .getPublicUrl(filePath)
    
    console.log(`✅ Image stored permanently: ${publicUrl}`)
    return publicUrl
    
  } catch (error) {
    console.error('❌ Error downloading and storing image:', error)
    return null
  }
}

 